import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { photoUrl } from "@/components/Portfolio";
import { FILTERS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = FILTERS.slice(1);

const inputCls =
    "w-full border border-stone-900/15 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-[#E75C15]";

const EMPTY = { title: "", location: "", year: "", specs: "", category: "ville", materials: "", quote: "" };

export const ProjectForm = ({ open, onClose, project, onSaved }) => {
    const [form, setForm] = useState(EMPTY);
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setForm(project ? {
            title: project.title,
            location: project.location,
            year: project.year,
            specs: project.specs || "",
            category: project.category,
            materials: project.materials || "",
            quote: project.quote || "",
        } : EMPTY);
        setPhotos(project?.photos ? [...project.photos] : []);
    }, [open, project]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
                setPhotos((ps) => [...ps, res.data.url]);
            }
            toast.success(files.length > 1 ? `${files.length} foto caricate.` : "Foto caricata.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Caricamento non riuscito.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.location.trim() || !form.year.trim()) {
            toast.error("Compila almeno titolo, località e anno.");
            return;
        }
        if (!photos.length) {
            toast.error("Carica almeno una foto del cantiere.");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, photos };
            const res = project
                ? await axios.patch(`${API}/projects/${project.id}`, payload, { withCredentials: true })
                : await axios.post(`${API}/projects`, payload, { withCredentials: true });
            toast.success(project ? "Cantiere aggiornato." : "Cantiere pubblicato nel portfolio.");
            onSaved(res.data);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Salvataggio non riuscito.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="project-form-dialog"
                className="max-h-[90vh] w-[94vw] max-w-2xl overflow-y-auto border border-[#E75C15]/30 bg-[#F9F7F2] p-0 sm:rounded-none"
            >
                <form onSubmit={save} className="p-8 sm:p-10">
                    <DialogTitle className="font-display text-3xl font-light text-stone-900">
                        {project ? "Modifica cantiere" : "Nuovo cantiere"}
                    </DialogTitle>

                    <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Titolo *</label>
                            <input data-testid="project-input-title" className={inputCls} placeholder="Es. Villa Il Poggio" value={form.title} onChange={set("title")} required />
                        </div>
                        <div>
                            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Località *</label>
                            <input data-testid="project-input-location" className={inputCls} placeholder="Es. Pietrasanta" value={form.location} onChange={set("location")} required />
                        </div>
                        <div>
                            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Anno consegna *</label>
                            <input data-testid="project-input-year" className={inputCls} placeholder="Es. 2025" value={form.year} onChange={set("year")} required />
                        </div>
                        <div>
                            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Specifiche</label>
                            <input data-testid="project-input-specs" className={inputCls} placeholder="Es. 320 mq · Ristrutturazione" value={form.specs} onChange={set("specs")} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Materiali</label>
                            <input data-testid="project-input-materials" className={inputCls} placeholder="Es. Cotto, rovere, pietra serena" value={form.materials} onChange={set("materials")} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Frase del cantiere</label>
                            <textarea
                                data-testid="project-input-quote"
                                className={`${inputCls} min-h-[80px] resize-y`}
                                placeholder="Es. Ogni dettaglio è stato curato come se fosse casa nostra."
                                value={form.quote}
                                onChange={set("quote")}
                            />
                            <p className="mt-1.5 text-xs leading-relaxed text-stone-400">
                                Citazione mostrata nella scheda del cantiere. Se la lasci vuota, viene usata la frase standard.
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <span className="mb-3 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">Categoria *</span>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORIES.map((c) => (
                                    <button
                                        type="button"
                                        key={c.id}
                                        data-testid={`project-category-${c.id}`}
                                        onClick={() => setForm((f) => ({ ...f, category: c.id }))}
                                        className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                                            form.category === c.id
                                                ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                                : "border-stone-900/20 text-stone-600 hover:border-[#E75C15]"
                                        }`}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6">
                        <span className="mb-3 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            Foto del cantiere * (la prima sarà la copertina)
                        </span>
                        <div className="flex flex-wrap gap-3">
                            {photos.map((p, i) => (
                                <div key={p + i} className="group relative h-24 w-24 overflow-hidden border border-stone-900/10">
                                    <img src={photoUrl(p)} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
                                    {i === 0 && (
                                        <span className="absolute bottom-0 left-0 right-0 bg-[#E75C15] text-center font-mono-alt text-[9px] uppercase tracking-wider text-[#1C1917]">
                                            Copertina
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`project-photo-remove-${i}`}
                                        onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))}
                                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#1C1917]/80 text-stone-100 opacity-0 transition-opacity group-hover:opacity-100"
                                        aria-label="Rimuovi foto"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                data-testid="project-photo-upload"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                className="flex h-24 w-24 flex-col items-center justify-center gap-1 border border-dashed border-stone-900/30 text-stone-500 transition-colors hover:border-[#E75C15] hover:text-[#C24E0F] disabled:opacity-50"
                            >
                                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                                <span className="font-mono-alt text-[9px] uppercase tracking-wider">
                                    {uploading ? "Carico…" : "Aggiungi"}
                                </span>
                            </button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                data-testid="project-photo-input"
                                onChange={onFiles}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={saving || uploading}
                        data-testid="project-save-button"
                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1C1917] px-6 py-4 text-sm font-medium text-stone-100 transition-colors duration-300 hover:bg-[#B8430D] disabled:opacity-60"
                    >
                        {saving ? (
                            <>
                                Salvo… <Loader2 className="h-4 w-4 animate-spin" />
                            </>
                        ) : project ? (
                            "Salva Modifiche"
                        ) : (
                            "Pubblica nel Portfolio"
                        )}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
